import ExcelJS from 'exceljs'
import { formatExportYearLabel, formatRate, formatSalary, splitEmployeeCount } from './formatters'

const HEAD_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE1E6ED' } }
const TOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } }
const DEFAULT_GENDER_LABELS = {
  all: '全労働者',
  regular: '正規雇用労働者',
  nonRegular: 'パート・有期労働者'
}

/**
 * @param {import('./types').EmployeeStatusYearData} data
 * @returns {import('./types').GenderLabels}
 */
function resolveGenderLabels(data) {
  return {
    all: data.genderLabels?.all || DEFAULT_GENDER_LABELS.all,
    regular: data.genderLabels?.regular || DEFAULT_GENDER_LABELS.regular,
    nonRegular: data.genderLabels?.nonRegular || DEFAULT_GENDER_LABELS.nonRegular
  }
}
const BORDER = {
  top: { style: 'thin', color: { argb: 'FF7476A6' } },
  bottom: { style: 'thin', color: { argb: 'FF7476A6' } },
  left: { style: 'thin', color: { argb: 'FF7476A6' } },
  right: { style: 'thin', color: { argb: 'FF7476A6' } }
}
const COMBINED_SHEET_COLUMN_COUNT = 30
const COMBINED_SHEET_COLUMN_WIDTH_PX = 45
const SUBMITTER_SHEET_COLUMN_COUNT = 8
/** 結合シート(30列)のセグメント表 14:8:8 を提出会社シート(8列)に按分 */
const SUBMITTER_SEGMENT_NAME_SPAN = 4
const SUBMITTER_SEGMENT_EMP_SPAN = 2
const SUBMITTER_SEGMENT_TEMP_SPAN = 2
const NO_DATA_MESSAGE = 'データ無し'
const DIVERSITY_SECTION_TITLE =
  '④ 管理職に占める女性労働者の割合、男性労働者の育児休業取得率及び労働者の男女の賃金の差異'

/**
 * Excel列幅（文字単位）へピクセルを変換（Excel標準換算: (px - 5) / 7）
 *
 * @param {number} pixels
 * @returns {number}
 */
function pixelsToColumnWidth(pixels) {
  return (pixels - 5) / 7
}

/**
 * @param {ExcelJS.Worksheet} sheet
 * @param {number} columnCount
 * @param {number} widthPx
 */
function setUniformColumnWidths(sheet, columnCount, widthPx) {
  const width = pixelsToColumnWidth(widthPx)
  for (let col = 1; col <= columnCount; col++) {
    sheet.getColumn(col).width = width
  }
}

/**
 * @param {ExcelJS.Cell} cell
 * @param {string | number} value
 * @param {{ bold?: boolean; fill?: object; align?: string }} [style]
 */
function setCell(cell, value, style = {}) {
  cell.value = value
  cell.font = { name: '游ゴシック', size: 11, bold: style.bold ?? false }
  cell.alignment = { vertical: 'middle', horizontal: style.align ?? 'center', wrapText: true }
  if (style.fill) cell.fill = style.fill
  if (style.border !== false) cell.border = BORDER
}

/**
 * @param {ExcelJS.Worksheet} sheet
 * @param {number} row
 * @param {number} [colSpan]
 * @returns {number}
 */
function writeNoDataMessage(sheet, row, colSpan = COMBINED_SHEET_COLUMN_COUNT) {
  sheet.mergeCells(row, 1, row, colSpan)
  setCell(sheet.getCell(row, 1), NO_DATA_MESSAGE, { align: 'left', border: false })
  return row + 1
}

/**
 * @param {ExcelJS.Worksheet} sheet
 * @param {number} row
 * @returns {number}
 */
function writeNoDataMessageSimple(sheet, row) {
  setCell(sheet.getCell(row, 1), NO_DATA_MESSAGE, { align: 'left', border: false })
  return row + 1
}

/**
 * @param {import('./types').EmployeeStatusYearData} data
 * @param {import('./types').SubmitterRow[]} sortedSubmitter
 * @param {'yen' | 'thousand-yen'} unit
 * @param {string} yearLabel
 * @param {{ hideConsolidated?: boolean }} [options]
 */
export async function exportEmployeeStatusToExcel(data, sortedSubmitter, unit, yearLabel, options = {}) {
  const hideConsolidated = options.hideConsolidated === true
  const unitLabel = unit === 'yen' ? '円' : '千円'
  const salaryHeaderLabel = `平均年間給与(${unitLabel})`
  const workbook = new ExcelJS.Workbook()

  addCombinedSheet(workbook, data, sortedSubmitter, unit, salaryHeaderLabel, hideConsolidated)
  if (!hideConsolidated) {
    addConsolidatedSheet(workbook, data)
  }
  addSubmitterSheet(workbook, data, sortedSubmitter, unit, salaryHeaderLabel)
  addDiversitySheet(workbook, data)

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  const filenameYear = formatExportYearLabel(yearLabel)
  link.href = url
  link.download = `${filenameYear}_有価証券報告書_従業員の状況.xlsx`
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * @param {ExcelJS.Workbook} workbook
 * @param {import('./types').EmployeeStatusYearData} data
 * @param {import('./types').SubmitterRow[]} sortedSubmitter
 * @param {'yen' | 'thousand-yen'} unit
 * @param {string} salaryHeaderLabel
 * @param {boolean} [hideConsolidated=false] Default is `false`
 */
function addCombinedSheet(workbook, data, sortedSubmitter, unit, salaryHeaderLabel, hideConsolidated = false) {
  const sheet = workbook.addWorksheet('従業員の状況')
  let row = 1

  sheet.mergeCells(row, 1, row, 15)
  setCell(sheet.getCell(row, 1), '【従業員の状況】', { align: 'left', border: false, bold: true })
  sheet.mergeCells(row, 21, row, 30)
  setCell(sheet.getCell(row, 21), data.snapshotDate, { align: 'right', border: false })
  row += 2

  if (!hideConsolidated) {
    sheet.mergeCells(row, 1, row, 30)
    setCell(sheet.getCell(row, 1), '① 連結会社の状況', { align: 'left', border: false, bold: true })
    row += 1

    if ((data.consolidated?.rows?.length ?? 0) === 0) {
      row = writeNoDataMessage(sheet, row)
    } else {
      sheet.mergeCells(row, 1, row, 14)
      setCell(sheet.getCell(row, 1), 'セグメントの名称', { bold: true, fill: HEAD_FILL })
      sheet.mergeCells(row, 15, row, 22)
      setCell(sheet.getCell(row, 15), '従業員数(名)', { bold: true, fill: HEAD_FILL })
      sheet.mergeCells(row, 23, row, 30)
      setCell(sheet.getCell(row, 23), '臨時従業員数(名)', { bold: true, fill: HEAD_FILL })
      row += 1

      data.consolidated.rows.forEach((r) => {
        const c = splitEmployeeCount(r.count)
        sheet.mergeCells(row, 1, row, 14)
        setCell(sheet.getCell(row, 1), r.name, { align: 'left' })
        sheet.mergeCells(row, 15, row, 22)
        setCell(sheet.getCell(row, 15), c.main)
        sheet.mergeCells(row, 23, row, 30)
        setCell(sheet.getCell(row, 23), c.temp)
        row += 1
      })
      const ct1 = splitEmployeeCount(data.consolidated.total.count)
      sheet.mergeCells(row, 1, row, 14)
      setCell(sheet.getCell(row, 1), data.consolidated.total.name, { bold: true, fill: TOTAL_FILL })
      sheet.mergeCells(row, 15, row, 22)
      setCell(sheet.getCell(row, 15), ct1.main, { bold: true, fill: TOTAL_FILL })
      sheet.mergeCells(row, 23, row, 30)
      setCell(sheet.getCell(row, 23), ct1.temp, { bold: true, fill: TOTAL_FILL })
      row += 1
    }
    row += 1
  }

  sheet.mergeCells(row, 1, row, 20)
  setCell(sheet.getCell(row, 1), '② 提出会社の状況 / ③ 最大人員会社の状況', {
    align: 'left',
    border: false,
    bold: true
  })
  sheet.mergeCells(row, 26, row, 30)
  setCell(sheet.getCell(row, 26), `金額単位：${unit === 'yen' ? '円' : '千円'}`, { align: 'right', border: false })
  row += 1

  const colSpec = [
    { label: '会社名', span: 6 },
    { label: '会社区分', span: 3 },
    { label: '従業員数(名)', span: 3 },
    { label: '臨時従業員数(名)', span: 3 },
    { label: '平均年齢(歳)', span: 3 },
    { label: '平均勤続年数(年)', span: 3 },
    { label: salaryHeaderLabel, span: 4 },
    { label: '平均年間給与の対前事業年度増減率（％）', span: 5 }
  ]

  if (sortedSubmitter.length === 0) {
    row = writeNoDataMessage(sheet, row)
  } else {
    let cur = 1
    colSpec.forEach((c) => {
      sheet.mergeCells(row, cur, row, cur + c.span - 1)
      setCell(sheet.getCell(row, cur), c.label, { bold: true, fill: HEAD_FILL })
      cur += c.span
    })
    row += 1

    sortedSubmitter.forEach((sm) => {
      const emp = splitEmployeeCount(sm.employeesDisplay)
      const values = [
        sm.name,
        sm.kindLabel,
        emp.main,
        emp.temp,
        sm.age,
        sm.tenure,
        formatSalary(sm.salary, unit),
        formatRate(sm.rate)
      ]
      cur = 1
      values.forEach((val, idx) => {
        sheet.mergeCells(row, cur, row, cur + colSpec[idx].span - 1)
        setCell(sheet.getCell(row, cur), val, { align: idx === 0 ? 'left' : 'center' })
        cur += colSpec[idx].span
      })
      row += 1
    })
  }

  if ((data.segmentBreakdown?.rows?.length ?? 0) === 0) {
    row += 1
    row = writeNoDataMessage(sheet, row)
  } else {
    if (sortedSubmitter.length > 0) {
      row += 1
    }
    sheet.mergeCells(row, 1, row, 14)
    setCell(sheet.getCell(row, 1), 'セグメントの名称', { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(row, 15, row, 22)
    setCell(sheet.getCell(row, 15), '従業員数(名)', { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(row, 23, row, 30)
    setCell(sheet.getCell(row, 23), '臨時従業員数(名)', { bold: true, fill: HEAD_FILL })
    row += 1

    data.segmentBreakdown.rows.forEach((r) => {
      const c = splitEmployeeCount(r.count)
      sheet.mergeCells(row, 1, row, 14)
      setCell(sheet.getCell(row, 1), r.name, { align: 'left' })
      sheet.mergeCells(row, 15, row, 22)
      setCell(sheet.getCell(row, 15), c.main)
      sheet.mergeCells(row, 23, row, 30)
      setCell(sheet.getCell(row, 23), c.temp)
      row += 1
    })
    const ct2 = splitEmployeeCount(data.segmentBreakdown.total.count)
    sheet.mergeCells(row, 1, row, 14)
    setCell(sheet.getCell(row, 1), data.segmentBreakdown.total.name, { bold: true, fill: TOTAL_FILL })
    sheet.mergeCells(row, 15, row, 22)
    setCell(sheet.getCell(row, 15), ct2.main, { bold: true, fill: TOTAL_FILL })
    sheet.mergeCells(row, 23, row, 30)
    setCell(sheet.getCell(row, 23), ct2.temp, { bold: true, fill: TOTAL_FILL })
    row += 1
  }
  row += 1

  sheet.mergeCells(row, 1, row, 30)
  setCell(sheet.getCell(row, 1), DIVERSITY_SECTION_TITLE, { align: 'left', border: false, bold: true })
  row += 1

  if ((data.diversity?.length ?? 0) === 0) {
    row = writeNoDataMessage(sheet, row)
  } else {
    const genderLabels = resolveGenderLabels(data)
    const headRow1 = row
    const headRow2 = row + 1
    sheet.mergeCells(headRow1, 1, headRow2, 6)
    setCell(sheet.getCell(headRow1, 1), '会社名', { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow1, 7, headRow2, 10)
    setCell(sheet.getCell(headRow1, 7), '管理職に占める女性労働者の割合(%)', { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow1, 11, headRow1, 20)
    setCell(sheet.getCell(headRow1, 11), '男性労働者の育児休業取得率(%)', { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow1, 21, headRow1, 30)
    setCell(sheet.getCell(headRow1, 21), '労働者の男女の賃金の差異(%)', { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow2, 11, headRow2, 13)
    setCell(sheet.getCell(headRow2, 11), genderLabels.all, { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow2, 14, headRow2, 16)
    setCell(sheet.getCell(headRow2, 14), genderLabels.regular, { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow2, 17, headRow2, 20)
    setCell(sheet.getCell(headRow2, 17), genderLabels.nonRegular, { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow2, 21, headRow2, 23)
    setCell(sheet.getCell(headRow2, 21), genderLabels.all, { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow2, 24, headRow2, 26)
    setCell(sheet.getCell(headRow2, 24), genderLabels.regular, { bold: true, fill: HEAD_FILL })
    sheet.mergeCells(headRow2, 27, headRow2, 30)
    setCell(sheet.getCell(headRow2, 27), genderLabels.nonRegular, { bold: true, fill: HEAD_FILL })
    row = headRow2 + 1

    data.diversity.forEach((r4) => {
      sheet.mergeCells(row, 1, row, 6)
      setCell(sheet.getCell(row, 1), r4.name, { align: 'left' })
      sheet.mergeCells(row, 7, row, 10)
      setCell(sheet.getCell(row, 7), r4.mgmt)
      sheet.mergeCells(row, 11, row, 13)
      setCell(sheet.getCell(row, 11), r4.male[0])
      sheet.mergeCells(row, 14, row, 16)
      setCell(sheet.getCell(row, 14), r4.male[1])
      sheet.mergeCells(row, 17, row, 20)
      setCell(sheet.getCell(row, 17), r4.male[2])
      sheet.mergeCells(row, 21, row, 23)
      setCell(sheet.getCell(row, 21), r4.wage[0])
      sheet.mergeCells(row, 24, row, 26)
      setCell(sheet.getCell(row, 24), r4.wage[1])
      sheet.mergeCells(row, 27, row, 30)
      setCell(sheet.getCell(row, 27), r4.wage[2])
      row += 1
    })
  }

  setUniformColumnWidths(sheet, COMBINED_SHEET_COLUMN_COUNT, COMBINED_SHEET_COLUMN_WIDTH_PX)
}

/**
 * @param {ExcelJS.Workbook} workbook
 * @param {import('./types').EmployeeStatusYearData} data
 */
function addConsolidatedSheet(workbook, data) {
  const sheet = workbook.addWorksheet('① 連結会社の状況')
  if ((data.consolidated?.rows?.length ?? 0) === 0) {
    writeNoDataMessageSimple(sheet, 1)
    sheet.getColumn(1).width = 28
    return
  }

  const rows = [['セグメントの名称', '従業員数(名)', '臨時従業員数(名)']]
  data.consolidated.rows.forEach((r) => {
    const c = splitEmployeeCount(r.count)
    rows.push([r.name, c.main, c.temp])
  })
  const ct = splitEmployeeCount(data.consolidated.total.count)
  rows.push([data.consolidated.total.name, ct.main, ct.temp])
  fillSimpleSheet(sheet, rows, [28, 16, 18], 1)
  applyTotalRowStyle(sheet, rows.length, 3)
}

/**
 * 提出会社シートのセグメント内訳（結合シートと同じ列幅比率でセル結合）
 *
 * @param {ExcelJS.Worksheet} sheet
 * @param {number} startRow
 * @param {{ rows: { name: string; count: string }[]; total: { name: string; count: string } }} segmentBreakdown
 * @returns {number} 次に書き込む行番号
 */
function writeSubmitterSegmentTable(sheet, startRow, segmentBreakdown) {
  const empCol = SUBMITTER_SEGMENT_NAME_SPAN + 1
  const tempCol = SUBMITTER_SEGMENT_NAME_SPAN + SUBMITTER_SEGMENT_EMP_SPAN + 1
  let row = startRow

  sheet.mergeCells(row, 1, row, SUBMITTER_SEGMENT_NAME_SPAN)
  setCell(sheet.getCell(row, 1), 'セグメントの名称', { bold: true, fill: HEAD_FILL })
  sheet.mergeCells(row, empCol, row, empCol + SUBMITTER_SEGMENT_EMP_SPAN - 1)
  setCell(sheet.getCell(row, empCol), '従業員数(名)', { bold: true, fill: HEAD_FILL })
  sheet.mergeCells(row, tempCol, row, SUBMITTER_SHEET_COLUMN_COUNT)
  setCell(sheet.getCell(row, tempCol), '臨時従業員数(名)', { bold: true, fill: HEAD_FILL })
  row += 1

  segmentBreakdown.rows.forEach((r) => {
    const c = splitEmployeeCount(r.count)
    sheet.mergeCells(row, 1, row, SUBMITTER_SEGMENT_NAME_SPAN)
    setCell(sheet.getCell(row, 1), r.name, { align: 'left' })
    sheet.mergeCells(row, empCol, row, empCol + SUBMITTER_SEGMENT_EMP_SPAN - 1)
    setCell(sheet.getCell(row, empCol), c.main)
    sheet.mergeCells(row, tempCol, row, SUBMITTER_SHEET_COLUMN_COUNT)
    setCell(sheet.getCell(row, tempCol), c.temp)
    row += 1
  })

  const totalCount = splitEmployeeCount(segmentBreakdown.total.count)
  sheet.mergeCells(row, 1, row, SUBMITTER_SEGMENT_NAME_SPAN)
  setCell(sheet.getCell(row, 1), segmentBreakdown.total.name, { bold: true, fill: TOTAL_FILL })
  sheet.mergeCells(row, empCol, row, empCol + SUBMITTER_SEGMENT_EMP_SPAN - 1)
  setCell(sheet.getCell(row, empCol), totalCount.main, { bold: true, fill: TOTAL_FILL })
  sheet.mergeCells(row, tempCol, row, SUBMITTER_SHEET_COLUMN_COUNT)
  setCell(sheet.getCell(row, tempCol), totalCount.temp, { bold: true, fill: TOTAL_FILL })

  return row + 1
}

/**
 * @param {ExcelJS.Workbook} workbook
 * @param {import('./types').EmployeeStatusYearData} data
 * @param {import('./types').SubmitterRow[]} sortedSubmitter
 * @param {'yen' | 'thousand-yen'} unit
 * @param {string} salaryHeaderLabel
 */
function addSubmitterSheet(workbook, data, sortedSubmitter, unit, salaryHeaderLabel) {
  const sheet = workbook.addWorksheet('② 提出会社の状況  ③ 最大人員会社の状況')
  const colWidths = [28, 12, 14, 16, 12, 14, 16, 22]
  const hasSubmitter = sortedSubmitter.length > 0
  const hasSegment = (data.segmentBreakdown?.rows?.length ?? 0) > 0
  let row = 1

  if (!hasSubmitter) {
    writeNoDataMessageSimple(sheet, row)
    row += 1
  } else {
    const headers = [
      '会社名',
      '会社区分',
      '従業員数(名)',
      '臨時従業員数(名)',
      '平均年齢(歳)',
      '平均勤続年数(年)',
      salaryHeaderLabel,
      '平均年間給与の対前事業年度増減率（％）'
    ]
    headers.forEach((label, idx) => {
      setCell(sheet.getCell(row, idx + 1), label, { bold: true, fill: HEAD_FILL })
    })
    row += 1

    sortedSubmitter.forEach((sm) => {
      const emp = splitEmployeeCount(sm.employeesDisplay)
      const values = [
        sm.name,
        sm.kindLabel,
        emp.main,
        emp.temp,
        sm.age,
        sm.tenure,
        formatSalary(sm.salary, unit),
        formatRate(sm.rate)
      ]
      values.forEach((val, idx) => {
        setCell(sheet.getCell(row, idx + 1), val, { align: idx === 0 ? 'left' : 'center' })
      })
      row += 1
    })
  }

  if (!hasSegment) {
    if (hasSubmitter) {
      row += 1
    }
    writeNoDataMessageSimple(sheet, row)
  } else {
    if (hasSubmitter) {
      row += 1
    }
    writeSubmitterSegmentTable(sheet, row, data.segmentBreakdown)
  }

  colWidths.forEach((wch, idx) => {
    sheet.getColumn(idx + 1).width = wch
  })
}

/**
 * @param {ExcelJS.Workbook} workbook
 * @param {import('./types').EmployeeStatusYearData} data
 */
function addDiversitySheet(workbook, data) {
  const sheet = workbook.addWorksheet('④ジェンダー統計情報')
  if ((data.diversity?.length ?? 0) === 0) {
    writeNoDataMessageSimple(sheet, 1)
    sheet.getColumn(1).width = 28
    return
  }

  const genderLabels = resolveGenderLabels(data)
  const rows = [
    [
      '会社名',
      '管理職に占める女性労働者の割合(%)',
      '男性労働者の育児休業取得率(%)',
      '',
      '',
      '労働者の男女の賃金の差異(%)',
      '',
      ''
    ],
    [
      '',
      '',
      genderLabels.all,
      genderLabels.regular,
      genderLabels.nonRegular,
      genderLabels.all,
      genderLabels.regular,
      genderLabels.nonRegular
    ]
  ]
  data.diversity.forEach((r4) => {
    rows.push([r4.name, r4.mgmt, r4.male[0], r4.male[1], r4.male[2], r4.wage[0], r4.wage[1], r4.wage[2]])
  })
  fillSimpleSheet(sheet, rows, [28, 18, 14, 14, 16, 14, 14, 16], 2)
  sheet.mergeCells(1, 1, 2, 1)
  sheet.mergeCells(1, 2, 2, 2)
  sheet.mergeCells(1, 3, 1, 5)
  sheet.mergeCells(1, 6, 1, 8)
}

/**
 * ヘッダー行のスタイル
 *
 * @param {ExcelJS.Worksheet} sheet
 * @param {number} rowIdx 1始まりの行番号
 * @param {number} colCount 対象列数
 */
function applyHeaderRowStyle(sheet, rowIdx, colCount) {
  for (let c = 1; c <= colCount; c++) {
    const cell = sheet.getCell(rowIdx, c)
    cell.font = { name: '游ゴシック', size: 11, bold: true }
    cell.fill = HEAD_FILL
    cell.border = BORDER
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  }
}

/**
 * 合計行のスタイル（シート『従業員の状況』の合計行と同じ）
 *
 * @param {ExcelJS.Worksheet} sheet
 * @param {number} rowIdx 1始まりの行番号
 * @param {number} colCount 対象列数
 */
function applyTotalRowStyle(sheet, rowIdx, colCount) {
  for (let c = 1; c <= colCount; c++) {
    const cell = sheet.getCell(rowIdx, c)
    cell.font = { name: '游ゴシック', size: 11, bold: true }
    cell.fill = TOTAL_FILL
    cell.border = BORDER
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  }
}

/**
 * @param {ExcelJS.Worksheet} sheet
 * @param {(string | number)[][]} rows
 * @param {number[]} colWidths
 * @param {number} headerRowCount
 */
function fillSimpleSheet(sheet, rows, colWidths, headerRowCount) {
  rows.forEach((row, rIdx) => {
    row.forEach((val, cIdx) => {
      const cell = sheet.getCell(rIdx + 1, cIdx + 1)
      const isHeader = rIdx < headerRowCount
      setCell(cell, val ?? '', {
        bold: isHeader,
        fill: isHeader ? HEAD_FILL : undefined,
        align: cIdx === 0 && !isHeader ? 'left' : 'center'
      })
    })
  })
  colWidths.forEach((wch, idx) => {
    sheet.getColumn(idx + 1).width = wch
  })
}
